import { useState, useEffect } from 'react'
import { X, User, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ImpersonationData {
  originalUser: {
    email: string
    name: string
    role: string
  }
  impersonatedUser: {
    email: string
    name: string
    role: string
    username: string
  }
  startTime: string
}

export function ImpersonationBar() {
  const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null)

  useEffect(() => {
    // Check for existing impersonation on mount
    const checkImpersonation = () => {
      const stored = localStorage.getItem('impersonation')
      if (stored) {
        try {
          const data = JSON.parse(stored)
          setImpersonationData(data)
        } catch (err) {
          console.error('Failed to parse impersonation data:', err)
          localStorage.removeItem('impersonation')
        }
      }
    }

    checkImpersonation()

    // Listen for impersonation events
    const handleImpersonationStart = (event: CustomEvent) => {
      setImpersonationData(event.detail)
    }

    const handleImpersonationEnd = () => {
      setImpersonationData(null)
    }

    window.addEventListener('impersonation-started', handleImpersonationStart as EventListener)
    window.addEventListener('impersonation-ended', handleImpersonationEnd)

    return () => {
      window.removeEventListener('impersonation-started', handleImpersonationStart as EventListener)
      window.removeEventListener('impersonation-ended', handleImpersonationEnd)
    }
  }, [])

  const handleEndImpersonation = () => {
    if (!confirm('End impersonation and return to your original account?')) return

    // Remove impersonation data
    localStorage.removeItem('impersonation')
    
    // Trigger event
    window.dispatchEvent(new CustomEvent('impersonation-ended'))
    
    // Refresh page to return to original user
    window.location.reload()
  }

  if (!impersonationData) return null

  const duration = new Date().getTime() - new Date(impersonationData.startTime).getTime()
  const minutes = Math.floor(duration / 60000)

  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-3 shadow-lg">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">IMPERSONATING:</span>
            <User className="h-4 w-4" />
            <span className="font-semibold">
              {impersonationData.impersonatedUser.name} ({impersonationData.impersonatedUser.email})
            </span>
            <span className="opacity-90">•</span>
            <span className="opacity-90">
              {minutes > 0 ? `${minutes} min${minutes !== 1 ? 's' : ''}` : 'Just started'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-xs opacity-90">
            Original: {impersonationData.originalUser.name}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEndImpersonation}
            className="text-white hover:bg-white/20 h-8 px-3"
          >
            <X className="h-4 w-4 mr-1" />
            End Impersonation
          </Button>
        </div>
      </div>
    </div>
  )
}